import React, { useEffect, useState, useMemo, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../style/custommillingHubdashboard.css";
import "../../style/designer-milling-responsive.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DataTable, { TableColumn } from "react-data-table-component";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SearchBox from "../CentralHub/Designer/SearchBox";
import SortFilter from "../CentralHub/Designer/SortFilter";
import { getAuthToken, getUserIdFromToken } from "../../utils/auth";
import { OrderRow } from "../MillingHub/Interface/OrderRow";
import { OrderDetailRow } from "../MillingHub/Interface/OrderDetailRow";
import { RejectionReason } from "../MillingHub/Interface/RejectionReason";
import { apiAxios } from "../../utils/apiAxios";
import AlertSoundManager from "../../utils/AlertSoundManager";

const MillingHubDashboard: React.FC = () => {

  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [orderDetails, setOrderDetails] = useState<OrderDetailRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderRow | null>(null);
  const [expiredCases, setExpiredCases] = useState<Set<string>>(() => {
    const saved = localStorage.getItem("expiredCases");
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [activeModalTab, setActiveModalTab] = useState<"order-review" | "actions">("order-review");
  const [showRejectUI, setShowRejectUI] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [reportDelayDateTime, setReportDelayDateTime] = useState<Date | null>(null);
  const [showReportDelayUI, setShowReportDelayUI] = useState(false);
  const [showReportreadyUI, setShowReportreadyUI] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [rejectionReasons, setRejectionReasons] = useState<RejectionReason[]>([]);
  const [selectedRejectReasons, setSelectedRejectReasons] = useState<number[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  const [selectedMachine, setSelectedMachine] = useState<number | null>(null);
  const [showMachineUI, setShowMachineUI] = useState(false);
  const [showRejectScanUI, setShowRejectScanUI] = useState(false);
  const [selectedRejectReasonId, setSelectedRejectReasonId] = useState<number | null>(null);
  const [showReportIssueUI, setShowReportIssueUI] = useState(false);
  const [issueText, setIssueText] = useState("");
  const [deliveryOtp, setDeliveryOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [showResend, setShowResend] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const isActionAvailable = selectedOrder
    ? (
      selectedOrder.status === "To be Accepted" ||
      selectedOrder.status === "Acceptance Overdue" ||
      selectedOrder.status === "Start Milling" ||
       selectedOrder.status === "Milling Overdue" ||  // ADD
      selectedOrder.status === "In Milling" ||
      selectedOrder.status === "Delay Report" ||
      selectedOrder.state === "READY_FOR_DELIVERY" ||
      selectedOrder.statusID === 15 ||
      selectedOrder.statusID === 9 ||
      selectedOrder.statusID === 8
    )
    : false;

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let interval: any;

    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    else if (otpTimer === 0 && otpSent) {
      setShowResend(true);
    }

    return () => clearInterval(interval);
  }, [otpTimer, otpSent]);
  useEffect(() => {
    const modalEl = document.getElementById("orderDetailsModal");
    if (!modalEl) return;

    const cleanupBody = () => {
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      document.documentElement.style.removeProperty("overflow");
      // remove any stray backdrop iOS sometimes leaves behind
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    };

    modalEl.addEventListener("hidden.bs.modal", cleanupBody);
    return () => modalEl.removeEventListener("hidden.bs.modal", cleanupBody);
  }, []);

  const [orderCounts, setOrderCounts] = useState({
    activeOrders: 0,
    pendingReview: 0,
    needAttention: 0,
    completed: 0,
  });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const API_PATH = process.env.REACT_APP_API_PATH_URL || "";

  const fetchDashboardOrders = async (statusFilter = "") => {
    try {
      setLoading(true);
      const token = getAuthToken();

      const res = await apiAxios.get(
        `${API_BASE_URL}/MillingHub/GetCasesByHub`,
        {
          params: { status: statusFilter },
          headers: { AuthorizationData: token },
        }
      );

      const apiData = res.data.data || [];

      const mappedData: OrderRow[] = apiData.map((item: any) => ({
        orderId: item.orderID,
        type: item.orderTypeName,
        state: item.underlyingOrderState,
        status: item.millingHubTag,
        caseID: item.caseID,
        statusID: item.statusID,
        createdDate: item.createdDate,
        millingAcceptanceDueOn: item.millingAcceptanceDueOn,
        deliveryMobile: item.deliveryMobile,
        filesList: item.filesList || [],
      }));

      setOrders(mappedData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRejectionReasons = async () => {
    try {
      const token = getAuthToken();
      const res = await apiAxios.get(
        `${API_BASE_URL}/RejectionReason/GetAll`,
        { headers: { AuthorizationData: token } }
      );

      if (res.data?.isSuccess && Array.isArray(res.data.singleData)) {
        setRejectionReasons(res.data.singleData.filter((x: any) => x.isActive));
      }
    } catch {
      toast.error("Failed to load rejection reasons");
    }
  };


  const handleStatusClick = (filter: string) => {
    fetchDashboardOrders(filter);
  };

  const toggleRejectReason = (id: number) => {
    setSelectedRejectReasons(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };


  const resetModalState = () => {
    setShowRejectUI(false);
    setShowRejectScanUI(false);
    setShowMachineUI(false);
    setShowReportDelayUI(false);
    setShowReportreadyUI(false);
    setRejectReason("");
    setIssueText("");
    setReportDelayDateTime(null);
    setSelectedRejectReasonId(null);
    setSelectedMachine(null);
    setDeliveryOtp("");
    setOtpSent(false);
    setOtpTimer(0);
    setShowResend(false);
    setResendCount(0);
    setSelectedOrder(null);
    setOrderDetails([]);
  };

  // Fetch order counts
  const fetchOrderCounts = async () => {
    try {
      const token = getAuthToken();
      const res = await apiAxios.get(`${API_BASE_URL}/MillingHub/GetOrderCounts`, {
        headers: { AuthorizationData: token },
      });
      const data = res.data?.data?.[0];
      if (data) {
        setOrderCounts({
          activeOrders: data.activeOrders ?? 0,
          pendingReview: data.pendingReview ?? 0,
          needAttention: data.needAttention ?? 0,
          completed: data.completed ?? 0,
        });
      }
    } catch (err) {
      console.error("Failed to load order counts", err);
    }
  };

  // Convert date to ISO string
  const toLocalISOString = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
  };

  // Accept order
  const acceptOrder = async () => {
    if (!selectedOrder) return;
    try {
      setActionLoading(true);
      const token = getAuthToken();
      await apiAxios.post(`${API_BASE_URL}/MillingHub/AcceptOrder`, { caseID: selectedOrder.caseID }, {
        headers: { AuthorizationData: token },
      });
      toast.success("Order accepted successfully");
      Modal.getInstance(document.getElementById("orderDetailsModal")!)?.hide();
      fetchDashboardOrders();
    } catch {
      toast.error("Failed to accept order");
    } finally {
      setActionLoading(false); // ✅ STOP LOADING
    }
  };

  const rejectOrder = async () => {
    if (!selectedOrder || !rejectReason.trim()) {
      toast.error("Please enter rejection reason");
      return;
    }

    try {
      setActionLoading(true);
      const token = getAuthToken();

      const res = await apiAxios.post(
        `${API_BASE_URL}/MillingHub/RejectOrder`,
        {
          caseID: selectedOrder.caseID,
          RejectReason: rejectReason,
        },
        {
          headers: { AuthorizationData: token },
        }
      );

      if (!res.data.isSuccess) {
        toast.error(res.data.status);
        return;
      }

      toast.success(res.data.status || "Order rejected successfully");

      setRejectReason("");
      setShowRejectUI(false);

      Modal.getInstance(
        document.getElementById("orderDetailsModal")!
      )?.hide();

      fetchDashboardOrders();
    } catch (err: any) {
      const msg =
        err?.response?.data?.status ||
        err?.response?.data?.message ||
        "Failed to reject order";

      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };


  const handleRejectScan = async () => {
    if (!selectedOrder || !selectedRejectReasonId) {
      toast.error("Please select rejection reason");
      return;
    }

    const selectedReasonObj = rejectionReasons.find(
      (r) => r.rejectionReasonID === selectedRejectReasonId
    );

    try {
      const token = getAuthToken();

      await apiAxios.post(
        `${API_BASE_URL}/MillingHub/RejectScan`,
        {
          caseID: selectedOrder.caseID,
          RejectReason: selectedReasonObj?.reasonName,
        },
        {
          headers: { AuthorizationData: token },
        }
      );

      toast.success("Scan rejected successfully");

      setShowRejectScanUI(false);
      setSelectedRejectReasonId(null);

      Modal.getInstance(
        document.getElementById("orderDetailsModal")!
      )?.hide();

      setSelectedOrder(null);

      fetchDashboardOrders();
    } catch {
      toast.error("Failed to reject scan");
    }
  };



  const reportDelay = async () => {
    if (!selectedOrder || !issueText.trim() || !reportDelayDateTime) {
      toast.error("Please enter issue details and select date/time");
      return;
    }

    try {
      setActionLoading(true);

      const token = getAuthToken();

      await apiAxios.post(
        `${API_BASE_URL}/MillingHub/ReportDelay`,
        {
          caseID: selectedOrder.caseID,
          delayReason: issueText,
          proposedNewDeliveryTime: reportDelayDateTime.toISOString(),
        },
        {
          headers: { AuthorizationData: token },
        }
      );

      toast.success("Delay reported successfully");

      setShowReportDelayUI(false);
      setIssueText("");
      setReportDelayDateTime(null);

      Modal.getInstance(
        document.getElementById("orderDetailsModal")!
      )?.hide();

      fetchDashboardOrders();
    } catch (error) {
      toast.error("Failed to report delay");
    } finally {
      setActionLoading(false);
    }
  };


  // Fetch order details
  const fetchOrderDetails = async (caseID: string) => {
    try {
      setDetailsLoading(true);
      const token = getAuthToken();
      const res = await apiAxios.get(`${API_BASE_URL}/MillingHub/GetCaseDetail/${caseID}`, {
        headers: { AuthorizationData: token },
      });
      const apiData = res.data.data || [];
      const mappedDetails: OrderDetailRow[] = apiData.map((item: any) => ({
        caseID: item.caseID,
        toothNumber: item.tooth,
        material: item.material,
        shade: item.shade,
        designFile: item.designFileName,
        imagePath: parseImagePath(item.imagePath),
        note: item.note || "",
        fileName: item.fileName || "",
        filePath: item.filePath || "",
        fileType: item.fileType || ""
      }));
      setOrderDetails(mappedDetails);
    } catch (err) {
      console.error("Failed to load order details", err);
    } finally {
      setDetailsLoading(false);
    }
  };
  const parseImagePath = (imagePath: any): string[] => {
    if (!imagePath) return [];

    // agar already array hai
    if (Array.isArray(imagePath)) {
      return imagePath.filter((f) => f && f.trim() !== "");
    }

    // agar string hai
    if (typeof imagePath === "string") {
      return imagePath
        .split(",")
        .map((f) => f.trim())
        .filter((f) => f !== "");
    }

    return [];
  };

  const getRemainingTime = (order: OrderRow) => {
    if (order.status !== "To be Accepted" || !order.millingAcceptanceDueOn) {
      return "--:--";
    }

    const dueTime = new Date(order.millingAcceptanceDueOn).getTime();
    const remaining = dueTime - now;

    if (remaining <= 0) return "00:00";

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const markCaseExpired = (caseID: string) => {
    setExpiredCases((prev) => {
      if (prev.has(caseID)) return prev;
      const updated = new Set(prev).add(caseID);
      localStorage.setItem("expiredCases", JSON.stringify(Array.from(updated)));
      return updated;
    });
  };


  const completeOrder = async () => {
    if (!selectedOrder) {
      toast.error("No order selected");
      return;
    }

    try {
      setActionLoading(true);

      const token = getAuthToken();

      await apiAxios.post(
        `${API_BASE_URL}/MillingHub/CompleteOrder`,
        {
          caseID: selectedOrder.caseID,
        },
        {
          headers: { AuthorizationData: token },
        }
      );

      toast.success("Order completed successfully");

      Modal.getInstance(
        document.getElementById("orderDetailsModal")!
      )?.hide();

      fetchDashboardOrders();
    } catch (error) {
      toast.error("Failed to complete order");
    } finally {
      setActionLoading(false);
    }
  };


  const resendDeliveryOtp = async () => {
    try {
      if (!selectedOrder) return;

      const token = getAuthToken();

      await apiAxios.post(
        `${API_BASE_URL}/MillingHub/ResendDeliveryOtp?caseId=${selectedOrder.caseID}`,
        {},
        {
          headers: { AuthorizationData: token },
        }
      );
      setDeliveryOtp("");
      const newCount = resendCount + 1;
      setResendCount(newCount);

      toast.success("OTP Resent Successfully");
      // setDeliveryOtp("");

      if (newCount >= 3) {
        setOtpTimer(1800); // 30 min
      } else {
        setOtpTimer(60);
      }

      setShowResend(false);
      setOtpSent(true);

    } catch {
      toast.error("Failed to resend OTP");
    }
  };

  const verifyDeliveryOtp = async () => {
    if (!selectedOrder || !deliveryOtp.trim()) {
      toast.error("Please enter OTP");
      return;
    }

    try {
      const token = getAuthToken();

      await apiAxios.post(
        `${API_BASE_URL}/MillingHub/VerifyDeliveryOtp?caseId=${selectedOrder.caseID}&mobile=${selectedOrder.deliveryMobile}&otp=${deliveryOtp}`,
        {},
        {
          headers: { AuthorizationData: token },
        }
      );

      toast.success("OTP verified successfully");

      setDeliveryOtp("");

      Modal.getInstance(
        document.getElementById("orderDetailsModal")!
      )?.hide();

      fetchDashboardOrders();
    } catch (error) {
      console.error(error);
      toast.error("Invalid OTP");
    }
  };

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };


  const filteredOrders = useMemo(() => {
    if (!searchText) return orders;

    return orders.filter((order) =>
      order.orderId?.toString().toLowerCase().includes(searchText.toLowerCase()) ||
      order.type?.toLowerCase().includes(searchText.toLowerCase()) ||
      order.status?.toLowerCase().includes(searchText.toLowerCase()) ||
      order.caseID?.toString().toLowerCase().includes(searchText.toLowerCase())
    );
  }, [orders, searchText]);

  const handleRowClick = async (row: OrderRow) => {
    setActiveModalTab("order-review");
    setShowRejectUI(false);
    setShowRejectScanUI(false);
    setShowMachineUI(false);
    setRejectReason("");
    setSelectedRejectReasonId(null);
    setSelectedMachine(null);
    setSelectedOrder(null);
    setTimeout(async () => {
      setSelectedOrder({ ...row });
      await fetchOrderDetails(row.caseID);

      const modalEl = document.getElementById("orderDetailsModal");
      if (modalEl) {
        const modal = new Modal(modalEl);
        modal.show();
      }
    }, 0);
  };


  // Timer update every second
  useEffect(() => {
    const interval = setInterval(() => {
      orders.forEach((order) => {
        if (order.statusID !== 1) return;
        const created = new Date(order.createdDate).getTime();
        if (Date.now() - created >= 10 * 60 * 1000 && !expiredCases.has(order.caseID)) {
          markCaseExpired(order.caseID);
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [orders, expiredCases]);

  // Initial load
  useEffect(() => {
    // initial load
    fetchDashboardOrders();
    fetchOrderCounts();

    // auto refresh every 30 sec when modal is closed
      const interval = setInterval(() => {
      const modalEl = document.getElementById("orderDetailsModal");
      const isModalOpen = modalEl?.classList.contains("show");

      if (!isModalOpen) {
        fetchDashboardOrders();
        fetchOrderCounts();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    if (selectedOrder?.state === "READY_FOR_DELIVERY") {
      setOtpTimer(60);
      setShowResend(false);
      setOtpSent(true);
      setResendCount(0);
    }
  }, [selectedOrder]);

  const getStatusStyle = (status: string) => {
    switch (status?.toLowerCase()) {


      case "to be accepted":
        return { background: "#6b7280", color: "#fff" };

      case "awaiting response":
        return { background: "#f97316", color: "#fff" };


      case "scan rejected":
        return { background: "#991b1b", color: "#fff" };

      case "milling hub rejected":
        return { background: "#991b1b", color: "#fff" };


      case "design delay":
        return { background: "#b45309", color: "#fff" };

      case "milling overdue":
        return { background: "#b45309", color: "#fff" };

      case "acceptance overdue":
        return { background: "#be123c", color: "#fff" };


      case "start milling":
        return { background: "#2563eb", color: "#fff" };


      case "waiting for design":
        return { background: "#f59e0b", color: "#000" };


      case "in milling":
        return { background: "#0d9488", color: "#fff" };

      case "done":
        return { background: "#16a34a", color: "#fff" };

      case "delay report":
        return { backgroundColor: "#b45309", color: "#fff" };
      default:
        return { background: "#6b7280", color: "#fff" };
    }
  };

  const openFile = (path: string) => window.open(`${API_PATH}${path}`, "_blank");
  const formatFileName = (fileName: string, maxChars = 6) => {
    const dotIndex = fileName.lastIndexOf(".");
    const name = dotIndex !== -1 ? fileName.substring(0, dotIndex) : fileName;
    const ext = dotIndex !== -1 ? fileName.substring(dotIndex) : "";
    if (name.length <= maxChars) return fileName;
    return `${name.substring(0, maxChars)}...${ext}`;
  };

  const columns: TableColumn<OrderRow>[] = [
    { name: "Order ID", center: true, selector: (row) => row.orderId, sortable: true },
    { name: "Type", center: true, selector: (row) => row.type, sortable: true },
    // { name: "State", selector: (row) => row.state, wrap: true },
    // { name: "Expected Delivery", cell: (row) => (row.statusID === 1 ?  <span>
    //                   Time: {selectedOrder ? getRemainingTime(selectedOrder) : "--:--"}
    //                 </span> : "--:--") },
    {
      name: "Time Remaining",
      center: true,
      cell: (row) => (
        row.millingAcceptanceDueOn
          ? <span className="text-danger fw-semibold"> {getRemainingTime(row)}</span>
          : "--:--"
      )
    }
    ,

    {
      name: "Status",
      center: true,
      cell: (row) => (
        <span
          style={{
            ...getStatusStyle(row.status),
            padding: "4px 8px",
            borderRadius: "5px",
            fontSize: "13px",
            width: "148px",
            cursor: "pointer",
            whiteSpace: "nowrap",
            fontWeight: "400",
            textAlign: "center"
          }}
          onClick={() => {
            setSelectedOrder(row);
            setActiveModalTab("order-review");
            fetchOrderDetails(row.caseID);
            new Modal(document.getElementById("orderDetailsModal")!).show();
          }}>
          {row.status}
        </span>
      ),
    },
  ];

  const fetchMachines = async () => {
    debugger
    try {
      const token = getAuthToken();
      const userId = getUserIdFromToken();

      if (!userId) {
        toast.error("User ID not found in token");
        return;
      }

      const res = await apiAxios.get(
        `${API_BASE_URL}/MillingHub/GetAllMachineNames?userId=${userId}`,
        {
          headers: { AuthorizationData: token, },
        }
      );

      setMachines(res.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load machines");
    }
  };

  const downloadZip = async (caseID: number, tooth: string) => {
    const key = `${caseID}-${tooth}`;

    try {
      setDownloadingKey(key);

      const token = getAuthToken();

      const response = await apiAxios.get(`/MillingHub/DownloadCaseFiles`, {
        params: { caseId: caseID },
        responseType: "blob",
        headers: { AuthorizationData: token || "" },
      });

      let fileName = `Case_${caseID}_Files.zip`;
      const disposition = response.headers["content-disposition"];

      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) fileName = match[1];
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success("File downloaded successfully ✅");

    } catch (error) {
      toast.error("Download failed");
    } finally {
      setDownloadingKey(null);
    }
  };

  const startMilling = async () => {

    if (!selectedOrder || !selectedMachine) {
      toast.error("Please select machine");
      return;
    }

    try {
      const token = getAuthToken();

      const formData = new FormData();
      formData.append("caseId", selectedOrder.caseID);
      formData.append("machineId", selectedMachine.toString());

      await apiAxios.post(`${API_BASE_URL}/MillingHub/start`, formData, {
        headers: {
          AuthorizationData: `Bearer ${token}`,
        },
      });
      toast.success("Milling started successfully");
      Modal.getInstance(
        document.getElementById("orderDetailsModal")!
      )?.hide();

      setShowMachineUI(false);
      setSelectedMachine(null);

      fetchDashboardOrders();
    } catch {
      toast.error("Failed to start milling");
    }
  };

  const [hasSOS, setHasSOS] = useState(false);

  const [audioEnabled, setAudioEnabled] = useState(false);
  const enableAudio = async () => {
    try {
      await AlertSoundManager.unlock();

      setAudioEnabled(true);
      localStorage.setItem("audioEnabled", "true");

      toast.success("Sound alerts enabled.");
    } catch {
      toast.error("Please allow sound playback.");
    }
  };




useEffect(() => {
  console.log("hasSOS changed:", hasSOS);

  if (!audioEnabled) return;

  if (hasSOS) {
    console.log("Playing sound...");
    AlertSoundManager.play();
  } else {
    console.log("Stopping sound...");
    AlertSoundManager.stop();
  }
}, [hasSOS, audioEnabled]);
useEffect(() => {
  const sos = filteredOrders.some(
    x =>
      x.status === "Acceptance Overdue" ||
      x.status === "To be Accepted" ||
      x.state === "DESIGN_READY"
  );


  setHasSOS(sos);
}, [filteredOrders]);

  return (
    <div style={{ overflowX: "hidden", maxWidth: "100vw", width: "100%" }}>
      {!audioEnabled && (
         <div className="audio-permission-overlay">
  <div className="audio-permission-box">
    <div className="audio-icon">🔔</div>

            <p>
              Click below to allow emergency sound alerts for
              <strong> Needs Attention</strong> orders.
            </p>

            <button
                 className="btn btn-danger enable-sound-btn"
              onClick={enableAudio}
            >
              Enable Sound Alerts
            </button>
          </div>
        </div>
      )}

      <div className="row mb-0 pb-0">
        <div className="col-md-12 mb-0 pb-0 mt-4">
          <div className={`main-header-section-top ${hasSOS ? "dashboard-sos" : ""}`}>

            <div className="order-status-bar">
              <div className="status-item"><div className="status-count">{orderCounts.activeOrders}</div><div onClick={() => handleStatusClick("Active")}>
                Active Orders
              </div></div>
              <div className="status-item">
                <div className="status-count">{orderCounts.pendingReview}</div>
                <div
                  className="status-badge warning"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleStatusClick("PendingReview")}
                >
                  <img src="../../awaiting.png" alt="icon" /> Pending Review
                </div>
              </div>
              <div className="status-item">
                <div className="status-count">{orderCounts.needAttention}</div>
                <div
                  className="status-badge danger"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleStatusClick("NeedAttention")}
                >
                  <img src="../../needsattantion2.png" alt="icon" />
                  Need Attention
                </div>
              </div>
              <div className="status-item">
                <div className="status-count">{orderCounts.completed}</div>
                <div
                  className="status-badge success"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleStatusClick("Completed")}
                >
                  <i className="bi bi-check-circle-fill completed-icon"></i> Completed
                </div>
              </div>
            </div>
          </div>
          <div className="shadow-container rounded p-3 mt-4">
            <div className="search-button-section  mb-3">
              <SearchBox value={searchText} onChange={setSearchText} />
              <SortFilter />
            </div>
            {/* iOS FIX: wrap DataTable so its internal columns scroll
                horizontally INSIDE this box instead of pushing the
                whole page width out on narrow screens */}
            <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <DataTable<OrderRow>
                columns={columns}
                data={filteredOrders}
                progressPending={loading}
                pagination
                highlightOnHover
                pointerOnHover
                noDataComponent="No orders found"
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 30]}
                onRowClicked={handleRowClick}

              />
            </div>
          </div>

          <div className="modal fade" id="orderDetailsModal" tabIndex={-1} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered only-desktop-center" style={{ maxWidth: "980px" }}>
              <div className="modal-content custom-modal-padding15px" style={{ minHeight: "580px" }}>
                <div className="modal-header manager-modal-header">
                  <h6 className="modal-title">Order ID: {selectedOrder?.orderId}</h6>
                  <button
                    type="button"
                    className="btn-close custom-close-btn"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={resetModalState}
                  >
                    <i className="bi bi-x customcross"></i>
                  </button>
                </div>
                <div className="modal-body p-0">
                  {/* Dynamic Header */}
                  <div className="d-flex justify-content-between px-3 py-1">
                    {activeModalTab === "order-review" ? (
                      <>
                        <div className="dentist-approval">{selectedOrder?.status}</div>
                        <span>
                          Time: {selectedOrder ? getRemainingTime(selectedOrder) : "--:--"}
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="dentist-approval">{selectedOrder?.status}</div>
                        <span>
                          Time: {selectedOrder ? getRemainingTime(selectedOrder) : "--:--"}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Tabs */}
                  <div className="d-flex  Actions-text-part border-bottom mx-3 order-details-tab-link pt-2 top-order-tabssection order-detail-bottom-border">
                    <a
                      className={`btn-link pop-upbtn-link px-0 border-bottom-second me-4 ${activeModalTab === "order-review"
                        ? "fw-semibold text-primary  border-2"
                        : "text-muted"
                        }`}
                      onClick={() => setActiveModalTab("order-review")}
                    >
                      Order Details
                    </a>

                    {!(
                      selectedOrder?.status === "Done" &&
                      !selectedOrder?.deliveryMobile?.trim()
                    ) && (
                        <a
                          className={` btn-link mx-4  pop-upbtn-link px-0 ${activeModalTab === "actions"
                            ? "fw-semibold text-primary border-2"
                            : "text-muted"
                            }`}
                          onClick={() => setActiveModalTab("actions")}
                        >
                          Actions
                        </a>
                      )}

                  </div>

                  {/* Order Review Tab */}
                  {activeModalTab === "order-review" && (
                    <div className="row g-0">
                      <div className="col-md-12 col-lg-9 p-3 col-12">


                     <div className="milling-table-wrapper">
  <table className="table table-borderless milling-custom-table">
    <thead>
      <tr>
        <th>Tooth</th>
        <th>Material</th>
        <th>Shade</th>
        <th>Design</th>
      </tr>
    </thead>
    <tbody>
      {detailsLoading ? (
        <tr>
          <td colSpan={4} className="text-center">
            Loading...
          </td>
        </tr>
      ) : orderDetails.length > 0 ? (
        orderDetails.map((item, index) => (
          <tr key={index}>
            <td>{item.toothNumber}</td>
            <td>{item.material}</td>
            <td>{item.shade}</td>
            <td>
              {selectedOrder &&
              (selectedOrder.status === "Start Milling" ||
                selectedOrder.status === "In Milling" ||
                selectedOrder.status === "Delay Report" ||
                selectedOrder.status === "Done") ? (
                <button
                  className="btn btn-primary btn-sm download-btn"
                  onClick={() =>
                    downloadZip(item.caseID, item.toothNumber)
                  }
                  disabled={
                    downloadingKey ===
                    `${item.caseID}-${item.toothNumber}`
                  }
                >
                  {downloadingKey ===
                  `${item.caseID}-${item.toothNumber}`
                    ? "Downloading..."
                    : "Download"}
                </button>
              ) : (
                <span>—</span>
              )}
            </td>
          </tr>
        ))
      ) : (
        <tr>
          <td colSpan={4} className="text-center">
            No design details found
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>
                        {/* <div className="download-center">
                          {selectedOrder?.status === "Start Milling" && (
                            <a
                              href="#"
                              className="text-decoration-underline-download-all mt-4"
                              onClick={() => {
                                selectedOrder.filesList?.forEach((file) => {
                                  if (Array.isArray(file.imagePath)) {
                                    file.imagePath.forEach((path) => openFile(path));
                                  } else if (file.imagePath) {
                                    openFile(file.imagePath);
                                  }
                                });
                              }}
                            >
                              Download
                            </a>
                          )}
                        </div> */}

                      </div>

                      <div className="col-md-12 col-lg-3 col-12 mt-3 border-start p-3">
                        <p className="scans-fontweight500">Scans (By Dentist)</p>
                        {selectedOrder?.filesList && selectedOrder.filesList.length > 0 ? (
                          selectedOrder.filesList.map((file, index) => (
                            <div key={file.fileID}>
                              <div className="d-flex justify-content-between align-items-center mt-2 px-2 py-1 border rounded">

                                <span
                                  className="text-truncate"
                                  style={{ cursor: "pointer" }}
                                  title={file.fileName}
                                  onClick={() => openFile(file.imagePath)}
                                >
                                  {formatFileName(file.fileName)}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">—</span>
                        )}

                        <p className="mt-3 scans-fontweight500">
                          Notes
                          <br />

                          <span
                            className="fw-normal note-scroll"
                            style={{
                              display: "block",
                              maxHeight: "80px",
                              overflowY: "auto",
                              paddingRight: "4px",
                            }}
                          >
                            {orderDetails?.[0]?.note || "—"}
                          </span>
                        </p>

                        <p className="scans-fontweight500">
                          File

                          <div className="file-partdesign">
                            {orderDetails?.[0]?.filePath ? (
                              <a
                                href={`${API_PATH}${orderDetails[0].filePath}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="fw-normal"
                                title={orderDetails[0].fileName}
                              >
                                {orderDetails[0].fileName}
                              </a>
                            ) : (
                              "—"
                            )}
                          </div>
                        </p>
                      </div>

                    </div>
                  )}

                  {/* Actions Tab */}
                  {activeModalTab === "actions" && selectedOrder && (
                    <>
                      {(selectedOrder.statusID === 15) && (
                        <>
                          {!showReportDelayUI && !showReportreadyUI && (
                            <div className="p-3">
                              <ul className="report-delay-list-action">
                                <li onClick={() => setShowReportDelayUI(true)}>
                                  Report Delay
                                </li>
                              </ul>

                              <ul className="report-delay-list-action">
                                <li onClick={() => setShowReportDelayUI(true)}>Cancel Order</li>
                              </ul>

                              <ul className="report-delay-list-action">
                                <li onClick={() => setShowReportreadyUI(true)}>
                                  Order Ready for Pickup
                                </li>
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                      {!isActionAvailable && (
                        <div className="p-4 text-center text-muted">
                          No actions available for this status
                        </div>
                      )}

                      {selectedOrder?.state === "READY_FOR_DELIVERY" && (
                        <div className="p-3">
                          <div className="fw-semibold text-center">Enter OTP</div>
                          {/* TIMER */}
                          {otpTimer > 0 && (
                            <p className="text-danger text-center pt-3">
                              Resend OTP in {formatTimer(otpTimer)}
                            </p>
                          )}


                          <div className="mb-3 pt-2">


                            <div
                              className="otp-container"
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "center",
                                gap: "8px",
                                maxWidth: "100%",
                              }}
                            >
                              {[...Array(6)].map((_, index) => (
                                <input
                                  key={index}
                                  type="text"
                                  maxLength={1}
                                  className="otp-box"
                                  value={deliveryOtp[index] || ""}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9]/g, "");
                                    const newOtp = deliveryOtp.split("");
                                    newOtp[index] = val;
                                    setDeliveryOtp(newOtp.join(""));

                                    // next focus
                                    if (val && e.target.nextSibling) {
                                      (e.target.nextSibling as HTMLInputElement).focus();
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "Backspace" &&
                                      !deliveryOtp[index] &&
                                      e.currentTarget.previousSibling
                                    ) {
                                      (e.currentTarget.previousSibling as HTMLInputElement).focus();
                                    }
                                  }}
                                />
                              ))}
                            </div>

                            {/* RESEND BUTTON */}
                            {showResend && (
                              <div className="text-center mb-2 pt-4">
                                <button
                                  className="btn btn-warning"
                                  onClick={resendDeliveryOtp}
                                >
                                  Resend OTP
                                </button>
                              </div>
                            )}
                          </div>

                          {!showResend && (
                            <div className="btn-section-center text-center pt-2 mb-2">
                              <button
                                className="btn btn-primary "
                                disabled={!deliveryOtp.trim()}
                                onClick={verifyDeliveryOtp}
                              >
                                Verify OTP
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {(selectedOrder.status === "To be Accepted" || selectedOrder.status === "Acceptance Overdue") && (
                        <div className="p-3">

                          {!showRejectUI ? (

                            <div className="btn-section-center mb-3">

                              <ul className="report-delay-list-action">

                                <li
                                  onClick={!actionLoading ? acceptOrder : undefined}
                                  style={{
                                    opacity: actionLoading ? 0.6 : 1,
                                    pointerEvents: actionLoading ? "none" : "auto",
                                  }}
                                >
                                  {actionLoading ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                      ></span>
                                      Processing...
                                    </>
                                  ) : (
                                    "Accept Order"
                                  )}
                                </li>
                              </ul>
                              <ul className="report-delay-list-action ">
                                <li onClick={() => setShowRejectUI(true)}>  Reject Order</li>
                              </ul>
                              {/* <button
                                className="btn report-dealybottom-text w-100"
                                onClick={() => setShowRejectUI(true)}
                              >
                                Reject Order
                              </button> */}
                            </div>
                          ) : (
                            <>
                              <div className="fw-semibold selectreasons mb-2">
                                Enter Rejection Reason
                              </div>

                              <textarea
                                className="form-control mb-3"
                                rows={3}
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter reason for rejection"
                              />

                              <div className="d-flex mb-2">

                                <button
                                  className="btn btn-primary"
                                  disabled={!rejectReason.trim() || actionLoading}
                                  onClick={rejectOrder}
                                >
                                  {actionLoading ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                      ></span>
                                      Processing...
                                    </>
                                  ) : (
                                    "Confirm Reject Order"
                                  )}
                                </button>
                                <button
                                  className="btn btn-secondary mx-2"
                                  style={{ cursor: "pointer" }} onClick={() => {
                                    setShowRejectUI(false);
                                    setRejectReason("");
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>


                            </>
                          )}
                        </div>
                      )}
                      {(selectedOrder?.status === "Start Milling" || selectedOrder?.status === "Milling Overdue")  && (
                        <div className="p-3">

                          {!showMachineUI ? (
                            <>
                              <ul className="report-delay-list-action">
                                <li
                                  onClick={() => {
                                    setShowMachineUI(true);
                                    fetchMachines();
                                  }}
                                >
                                  Start Milling
                                </li>
                              </ul>

                              {!showRejectScanUI ? (
                                <div className="btn-section-center">
                                  <button
                                    className="btn report-dealybottom-text w-100"
                                    onClick={() => {
                                      setShowRejectScanUI(true);
                                      fetchRejectionReasons();
                                    }}
                                  >
                                    Reject Design
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="fw-semibold selectreasons mt-3 mb-2">
                                    SELECT REJECTION REASON
                                  </div>

                                  <select
                                    className="form-select mb-3"
                                    value={selectedRejectReasonId || ""}
                                    onChange={(e) =>
                                      setSelectedRejectReasonId(Number(e.target.value))
                                    }
                                  >
                                    <option value="">Select Reason</option>

                                    {rejectionReasons.map((reason) => (
                                      <option
                                        key={reason.rejectionReasonID}
                                        value={reason.rejectionReasonID}
                                      >
                                        {reason.reasonName}
                                      </option>
                                    ))}
                                  </select>

                                  <div className="btn-section-center">
                                    <button
                                      className="btn btn-primary"
                                      disabled={!selectedRejectReasonId}
                                      onClick={handleRejectScan}
                                    >
                                      Confirm Reject Scan
                                    </button>
                                    <button
                                      className="btn btn-secondary mx-2"
                                      onClick={() => {
                                        setShowRejectScanUI(false);
                                        setSelectedRejectReasonId(null);
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>




                                </>
                              )}
                            </>
                          ) : (

                            <>
                              <div className="fw-semibold mb-2">SELECT MACHINE</div>

                              <select
                                className="form-select mb-3"
                                value={selectedMachine || ""}
                                onChange={(e) => setSelectedMachine(Number(e.target.value))}
                              >
                                <option value=""></option>

                                {machines.map((machine) => (
                                  <option key={machine.machineID} value={machine.machineID}>
                                    {machine.companyName}
                                  </option>
                                ))}
                              </select>

                              <div className="btn-section-center">
                                <button
                                  className="btn report-dealybottom-text w-100"
                                  onClick={startMilling}
                                >
                                  Confirm Start Milling
                                </button>
                              </div>

                              <div className="text-center mt-2">
                                <span
                                  style={{ cursor: "pointer", color: "gray" }}
                                  onClick={() => {
                                    setShowMachineUI(false);
                                    setSelectedMachine(null);
                                  }}
                                >
                                  Cancel
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {(selectedOrder?.status === "In Milling" || selectedOrder?.status === "Delay Report") && (
                        <div className="p-3">


                          {selectedOrder?.status === "In Milling" && !showReportDelayUI && (
                            <ul className="report-delay-list-action">
                              <li onClick={() => setShowReportDelayUI(true)}>
                                Report Delay
                              </li>
                            </ul>
                          )}

                          {selectedOrder?.status === "In Milling" && showReportDelayUI && (
                            <div className="rounded">
                              <div className="d-flex align-items-center px-3 pb-3 border-bottom">
                                <span
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    setShowReportDelayUI(false);
                                    setIssueText("");
                                    setReportDelayDateTime(null);
                                  }}
                                >
                                  Back
                                </span>
                                <h6 className="ms-auto me-auto mb-0">Report Delay</h6>
                              </div>

                              <div className="content-inside-borderbody2 mt-2">
                                <label className="form-label">Enter Issue Details</label>

                                <textarea
                                  className="form-control mb-3"
                                  rows={3}
                                  value={issueText}
                                  onChange={(e) => setIssueText(e.target.value)}
                                  placeholder="Describe the issue"
                                />
                              </div>

                              <label className="form-label mb-2 ">
                                Select Expected Date & Time
                              </label>

                              <div className="date-picker-position-relate position-relative">
                                <DatePicker
                                  selected={reportDelayDateTime}
                                  onChange={(date) => setReportDelayDateTime(date)}
                                  showTimeSelect
                                  timeFormat="HH:mm"
                                  timeIntervals={15}
                                  dateFormat="dd/MM/yyyy h:mm aa"
                                  minDate={new Date()}
                                  className="form-control mb-3"
                                  placeholderText="Select date & time"
                                />
                                <i
                                  className="bi bi-calendar3"
                                  style={{
                                    position: "absolute",
                                    right: "15px",
                                    top: "36%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                    color: "#6c757d",
                                  }}
                                ></i>
                              </div>

                              <div className="pb-1">
                                <button
                                  className="btn btn-primary"
                                  disabled={!issueText.trim() || !reportDelayDateTime || actionLoading}
                                  onClick={reportDelay}
                                >
                                  {actionLoading ? "Processing..." : "Confirm Report Delay"}
                                </button>
                              </div>
                            </div>
                          )}

                          {!showReportDelayUI && (
                            <button
                              className="btn report-dealybottom-text w-100"
                              onClick={completeOrder}
                              disabled={actionLoading}
                            >
                              {actionLoading ? "Processing..." : "Complete Order"}
                            </button>
                          )}

                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default MillingHubDashboard;
